// Generate a grayscale brightness-field PNG from a variant + seed.
// Browser-only.
//
// This is what gets uploaded to Pinata for cover images: a smooth
// grayscale "shape mask" with NO palette / dither / dots applied. The
// lib then re-applies all formatting at display time via the standard
// image-overlay render path, so palette + dither + cellSize + algorithm
// changes propagate to every existing cover automatically.
//
// Variant + seed are LOCKED into the PNG at generation time — changes
// to the brightness functions later won't affect existing covers
// without re-generating + re-uploading.

import type { GenerativeVariant } from '../types';
import { buildDensityConfig, type DensityConfigOverrides } from './dot-engine/build-config';
import { hashSeed } from './dot-engine/math';
import { isDotStudioMode } from './dot-engine/types';
import { sampleGenerativeBrightness } from './generative-brightness';

export type GenerativePngMimeType = 'image/png' | 'image/jpeg' | 'image/webp';

export interface GenerateGenerativePngOptions {
  /** Output canvas width in pixels. Default 1200 (workspace canvas size). */
  readonly width?: number;
  /** Output canvas height in pixels. Default 400 (3:1 cover at width 1200). */
  readonly height?: number;
  /** Output MIME type. Default 'image/png'. */
  readonly mimeType?: GenerativePngMimeType;
  /** Compression quality (jpeg / webp only). Default 0.92. */
  readonly quality?: number;
  /** Dot-studio density overrides (symmetry, source layout, etc.). */
  readonly densityOverrides?: DensityConfigOverrides;
  /** Pre-built density config — skips `buildDensityConfig` when set. */
  readonly densityConfig?: ReturnType<typeof buildDensityConfig>;
}

const DEFAULT_WIDTH = 1200;
const DEFAULT_HEIGHT = 400;
const DEFAULT_QUALITY = 0.92;

/**
 * Render a variant's brightness field to a grayscale Blob ready for
 * upload to Pinata / IPFS / S3.
 *
 * Each pixel is the variant's `brightness(u, v)` value mapped to a
 * grayscale RGB. Alpha is fully opaque. The resulting PNG is the
 * "abstracted shape" — no palette, no dither, just smooth gradients —
 * so the formatter can re-style at any cellSize / palette / dither
 * algorithm at render time.
 *
 * Throws if `OffscreenCanvas` isn't available in the runtime (SSR /
 * unsupported browser).
 */
export async function generateGenerativePng(
  variant: GenerativeVariant,
  seed: number | string,
  options: GenerateGenerativePngOptions = {},
): Promise<Blob> {
  const width = options.width ?? DEFAULT_WIDTH;
  const height = options.height ?? DEFAULT_HEIGHT;
  const mimeType = options.mimeType ?? 'image/png';
  const quality = options.quality ?? DEFAULT_QUALITY;
  const densityConfig =
    options.densityConfig ??
    (isDotStudioMode(variant)
      ? buildDensityConfig(variant, seed, width, height, options.densityOverrides)
      : undefined);
  const legacySeed = typeof seed === 'number' ? seed : hashSeed(String(seed));

  if (typeof OffscreenCanvas === 'undefined') {
    throw new Error('pattern-studio: OffscreenCanvas not available in this runtime');
  }

  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    throw new Error('pattern-studio: failed to acquire 2D context on OffscreenCanvas');
  }

  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;

  // Iterate every pixel — variant function is mathematically continuous,
  // so any output resolution is valid. Higher resolution = more detail
  // preserved when the consumer re-samples at a different cellSize.
  const wDiv = width <= 1 ? 1 : width - 1;
  const hDiv = height <= 1 ? 1 : height - 1;
  for (let y = 0; y < height; y++) {
    const v = y / hDiv;
    for (let x = 0; x < width; x++) {
      const u = x / wDiv;
      const b = sampleGenerativeBrightness(u, v, variant, legacySeed, width, height, densityConfig);
      const grey = Math.round(b * 255);
      const idx = (y * width + x) * 4;
      data[idx] = grey;
      data[idx + 1] = grey;
      data[idx + 2] = grey;
      data[idx + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas.convertToBlob({ type: mimeType, quality });
}
